// Custom Stage Task page - displays and allows submission of custom stage forms

import { CustomStageForm } from "@/components/custom-stages";
import { ProjectPageHeader } from "@/components/projects/project-page-header";
import { captureEvent } from "@/lib/analytics-loader";
import {
  useGetFileUploadUrlMutation,
  useTaskDetailsQuery,
  useUpdateTaskDetailsMutation,
  type CustomStageTaskDetails,
  type FieldValueInput,
} from "@/lib/queries/custom-stages";
import type { CampaignRouteContext } from "@/lib/types/route-context";
import { Card, CardContent, CardHeader, Skeleton, toast } from "@repo/ui";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ClipboardList } from "lucide-react";
import type { ReactNode } from "react";

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/custom-task/$stageId",
)({
  beforeLoad: ({ context, params }) => {
    const { profile, organization, programId } =
      context as CampaignRouteContext;
    const stageId = Number(params.stageId);

    if (isNaN(stageId)) {
      throw redirect({
        to: "/o/$orgSlug/p/$profileSlug/c/$campaignId",
        params: {
          orgSlug: organization.slug,
          profileSlug: profile.slug,
          campaignId: String(programId),
        },
      });
    }
  },
  component: CustomTaskPage,
});

// ============================================================================
// Presentational View (exported for Storybook)
// ============================================================================

export interface CustomTaskPageViewProps {
  isLoading: boolean;
  error: Error | null;
  taskDetails: CustomStageTaskDetails | null;
  onSubmit: (fields: FieldValueInput[]) => void;
  isSubmitting: boolean;
  onGetUploadUrl: (
    fileName: string,
    contentType: string,
  ) => Promise<{ fileName: string; uploadUrl: string } | null>;
}

export function CustomTaskPageView({
  isLoading,
  error,
  taskDetails,
  onSubmit,
  isSubmitting,
  onGetUploadUrl,
}: CustomTaskPageViewProps): ReactNode {
  if (isLoading) {
    return <CustomTaskPageSkeleton />;
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <ProjectPageHeader title="Custom Task" />
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <p className="text-destructive font-medium">
              Failed to load task details
            </p>
            <p className="text-muted-foreground text-sm mt-2">
              {error.message || "Please try again later."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!taskDetails) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <ProjectPageHeader title="Custom Task" />
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Task not found. Please contact support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if creator has access
  if (!taskDetails.creatorAccess) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <ProjectPageHeader title="Custom Task" />
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              You don't have access to this task.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-6">
      {/* Header */}
      <ProjectPageHeader title={taskDetails.stage?.name || "Custom Task"}>
        <ClipboardList className="h-6 w-6 text-muted-foreground" />
      </ProjectPageHeader>

      {/* Form */}
      <CustomStageForm
        taskDetails={taskDetails}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        onGetUploadUrl={onGetUploadUrl}
      />
    </div>
  );
}

// ============================================================================
// Container (hooks + data fetching)
// ============================================================================

function CustomTaskPage() {
  const { stageId: stageIdStr } = Route.useParams();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { campaign, programId, profile, organization } = context;
  const navigate = useNavigate();

  // stageId validated in beforeLoad — safe to parse here
  const stageId = parseInt(stageIdStr, 10);

  // Fetch task details
  const {
    data: taskDetails,
    isPending,
    error,
  } = useTaskDetailsQuery(organization.id, programId, stageId, profile.id);

  // Update mutation
  const updateMutation = useUpdateTaskDetailsMutation(organization.id);

  // File upload mutation
  const uploadUrlMutation = useGetFileUploadUrlMutation(organization.id);

  const handleSubmit = async (fields: FieldValueInput[]) => {
    try {
      await updateMutation.mutateAsync({
        programId,
        stageId,
        profileId: profile.id,
        fields,
      });

      captureEvent("custom_task_submitted", {
        organizationId: organization.id,
        stageId,
        profileId: profile.id,
      });

      toast.success("Form submitted successfully");

      // Navigate back to project page
      navigate({
        to: "/o/$orgSlug/p/$profileSlug/c/$campaignId",
        params: {
          orgSlug: organization.slug,
          profileSlug: campaign.profileSlug,
          campaignId: String(programId),
        },
      });
    } catch {
      toast.error("Failed to submit form. Please try again.");
    }
  };

  const handleGetUploadUrl = async (
    fileName: string,
    contentType: string,
  ): Promise<{ fileName: string; uploadUrl: string } | null> => {
    try {
      const result = await uploadUrlMutation.mutateAsync({
        profileId: profile.id,
        fileName,
        contentType,
      });
      return result;
    } catch {
      return null;
    }
  };

  return (
    <CustomTaskPageView
      isLoading={isPending}
      error={error}
      taskDetails={taskDetails ?? null}
      onSubmit={handleSubmit}
      isSubmitting={updateMutation.isPending}
      onGetUploadUrl={handleGetUploadUrl}
    />
  );
}

function CustomTaskPageSkeleton() {
  return (
    <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-6">
      <div className="flex items-center gap-4">
        {/* BackButton removed */}
        <div className="flex-1">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>

      {/* Form skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-full max-w-md" />
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Multiple form fields */}
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
          <Skeleton className="h-10 w-full mt-4" />
        </CardContent>
      </Card>
    </div>
  );
}
